import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type {
  Alternativelodgingsummary,
  CompareShortTermRentalOccupancy,
  CompareShortTermRentalRate,
  Countrysearch,
  Demand,
  Event,
  Holiday,
  Lossearch,
  Occupancy,
  Outlier,
  Period,
  PredictedOccupancy,
  Rateindex,
  RoomOccupancy,
  Searchsummary,
  ShortTermRentalOccupancy,
  ShortTermRentalRate,
  Summary,
} from 'lumen/models';
import type Recommendation from 'lumen/models/marketing/recommendation';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import selectMostRecentExtractDateSummary from 'lumen/utils/market-insight/market-insight-summary-helpers';

export default class Day extends Model {
  [Type] = 'day' as const;

  @belongsTo('demand', { async: false, inverse: 'day' })
  declare demand: Demand;

  @hasMany('alternativelodgingsummary', { async: false, inverse: 'day' })
  declare alternativeLodgingSummaries: HasMany<Alternativelodgingsummary>;

  @hasMany('compare-short-term-rental-occupancy', {
    async: false,
    inverse: 'day',
  })
  declare compareShortTermRentalOccupancies: HasMany<CompareShortTermRentalOccupancy>;

  @hasMany('compare-short-term-rental-rate', { async: false, inverse: 'day' })
  declare compareShortTermRentalRates: HasMany<CompareShortTermRentalRate>;

  @hasMany('countrysearch', { async: false, inverse: 'day' })
  declare countrysearches: HasMany<Countrysearch>;

  @hasMany('event', { async: false, inverse: 'days' })
  declare events: HasMany<Event>;

  @hasMany('holiday', { async: false, inverse: 'days' })
  declare holidays: HasMany<Holiday>;

  @hasMany('lossearch', { async: false, inverse: 'day' })
  declare lossearches: HasMany<Lossearch>;

  @hasMany('marketing/recommendation', { async: false, inverse: 'days' })
  declare marketingRecommendations: HasMany<Recommendation>;

  @hasMany('occupancy', { async: false, inverse: 'day' })
  declare occupancies: HasMany<Occupancy>;

  @hasMany('outlier', { async: false, inverse: 'day' })
  declare outliers: HasMany<Outlier>;

  @hasMany('period', { async: false, inverse: 'day' })
  declare periods: HasMany<Period>;

  @hasMany('predicted-occupancy', { async: false, inverse: 'day' })
  declare predictedOccupancies: HasMany<PredictedOccupancy>;

  // Market Insight
  @hasMany('rateindex', { async: false, inverse: 'day' })
  declare rateIndexes: HasMany<Rateindex>;

  @hasMany('short-term-rental-occupancy', { async: false, inverse: 'day' })
  declare rentalOccupancies: HasMany<ShortTermRentalOccupancy>;

  @hasMany('short-term-rental-rate', { async: false, inverse: 'day' })
  declare rentalRates: HasMany<ShortTermRentalRate>;

  @hasMany('room-occupancy', { async: false, inverse: 'day' })
  declare roomOccupancies: HasMany<RoomOccupancy>;

  @hasMany('searchsummary', { async: false, inverse: 'day' })
  declare searchsummaries: HasMany<Searchsummary>;

  @hasMany('summary', { async: false, inverse: 'day' })
  declare summaries: HasMany<Summary>;

  @attr('hoteldate') declare date: NonNullable<HoteldateTransformReturn>;
  @attr('number') declare integerDate: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked isComplete?: Record<string, boolean> = {
    events: false,
    rates: false,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarEvents() {
    return this.events.filter((event) => event.isAccepted);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventsExceptRejected() {
    return this.events.filter((event) => event.status !== 'rejected');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredEvents() {
    return this.events.filter((event) => event.defaultVisible);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDate() {
    return this.date.format('YYYY-MM-DD');
  }

  get holidayCountries(): Holiday['countries'] | ['multiple'] {
    // creates list of unique countries, return 'multiple' if there are more than MAX_FLAGS
    let countries: Holiday['countries'] = [];
    if (this.holidays?.length) {
      this.holidays.forEach((holiday) => {
        countries = [...countries, ...holiday.countries];
      });
      countries = [...new Set(countries)];
      if (countries.length > 1) {
        return ['multiple'];
      }
    }
    return countries;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPast() {
    return this.hotelAndMarketService.isPast(this.date);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    const localTime = this.hotelAndMarketService.getLocalTime();
    return this.date.isSame(localTime, 'day');
  }

  // Get the summary with the most recent extract date, for the daily view
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mostRecentDailySummary() {
    let mostRecentSummary = null;

    if (this.summaries?.length) {
      const summariesAsArray = [...this.summaries].filter(
        (summary) => summary.isDailySummary,
      );
      mostRecentSummary = selectMostRecentExtractDateSummary(summariesAsArray);
    }

    return mostRecentSummary;
  }

  // Get the summary with the most recent extract date for the weekly view
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mostRecentWeeklySummary() {
    let mostRecentSummary = null;

    if (this.summaries?.length) {
      const summariesAsArray = [...this.summaries].filter(
        (summary) => summary.isWeeklySummary,
      );
      mostRecentSummary = selectMostRecentExtractDateSummary(summariesAsArray);
    }

    return mostRecentSummary;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancy() {
    const occupancies = this.occupancies?.filter(
      (occupancy) =>
        !occupancy.dateAggregation ||
        occupancy.dateAggregation === DATE_AGGREGATION.DAY,
    );
    return occupancies[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get predictedOccupancy() {
    const predictedOccupancies = this.predictedOccupancies?.filter(
      (predictedOccupancy) =>
        !predictedOccupancy.dateAggregation ||
        predictedOccupancy.dateAggregation === DATE_AGGREGATION.DAY,
    );
    return predictedOccupancies[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unacceptedEvents() {
    return this.events.filter(
      (event) => !event.isDeclined && !event.isAccepted,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weeklyOccupancy() {
    return this.occupancies?.filter(
      (occupancy) => occupancy.dateAggregation === DATE_AGGREGATION.WEEK,
    )[0];
  }

  // Get the summary type (flignts/hotel business/hotel leisure) search summary with the most recent extract date
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMostRecentSearchSummary(
    isDailyAggregation: boolean,
    summaryType: Searchsummary['type'],
  ) {
    let mostRecentSearchSummary = null;

    if (this.searchsummaries?.length) {
      let searchSummariesAsArray: Searchsummary[] = [];

      if (isDailyAggregation) {
        searchSummariesAsArray = [...this.searchsummaries].filter(
          (summary) => summary.type === summaryType,
        );
      } else {
        searchSummariesAsArray = [...this.searchsummaries].filter(
          (summary) =>
            summary.type === summaryType &&
            summary.dateAggregation === DATE_AGGREGATION.WEEK,
        );
      }
      mostRecentSearchSummary = selectMostRecentExtractDateSummary(
        searchSummariesAsArray,
      );
    }

    return mostRecentSearchSummary;
  }
}
